import {
  Alert,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Clear,
  PauseCircleOutline,
  PlayCircleOutline,
  VerticalAlignBottom,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";

import {
  LogLine,
  distributionService,
} from "../../services/distributionService";

interface TerminalLogTailProps {
  // Polling interval. 2000ms recomendado mientras hay run en ejecución.
  pollingMs?: number;
  // Si true (default), arranca el polling automáticamente al montar.
  autoStart?: boolean;
  // Si se pasa, filtra líneas por substring del context (case-insensitive).
  contextFilter?: string;
  // Altura máxima de la consola en px.
  maxHeightPx?: number;
}

const LEVEL_COLOR: Record<LogLine["level"], string> = {
  log: "#cccccc",
  warn: "#facc15",
  error: "#f87171",
  debug: "#94a3b8",
  verbose: "#a3e635",
};

function formatTs(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const mss = String(d.getMilliseconds()).padStart(3, "0");
  return `${hh}:${mm}:${ss}.${mss}`;
}

function logLineKey(line: LogLine): string {
  return `${line.ts}|${line.level}|${line.context}|${line.message}`;
}

export function TerminalLogTail({
  pollingMs = 2000,
  autoStart = true,
  contextFilter,
  maxHeightPx = 360,
}: TerminalLogTailProps): React.ReactElement {
  const [lines, setLines] = useState<LogLine[]>([]);
  const [isStreaming, setIsStreaming] = useState(autoStart);
  const [autoscroll, setAutoscroll] = useState(true);
  const [lastError, setLastError] = useState<string | null>(null);
  const sinceRef = useRef<number>(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isStreaming) return undefined;
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const { lines: incoming } =
          await distributionService.getPostSyncLogsTail(
            sinceRef.current,
            contextFilter,
          );
        if (cancelled) return;
        setLastError(null);
        if (incoming.length > 0) {
          setLines((prev) => {
            const byKey = new Map<string, LogLine>();
            for (const line of [...prev, ...incoming]) {
              byKey.set(logLineKey(line), line);
            }
            const merged = Array.from(byKey.values()).sort(
              (a, b) => a.ts - b.ts,
            );
            // Cap a 2000 líneas en pantalla para no inflar DOM
            return merged.length > 2000 ? merged.slice(-2000) : merged;
          });
          const maxIncomingTs = Math.max(...incoming.map((line) => line.ts));
          sinceRef.current = Math.max(0, maxIncomingTs - 1);
        }
      } catch (error) {
        if (cancelled) return;
        const message =
          error instanceof Error ? error.message : "error desconocido";
        setLastError(`No se pudieron cargar logs: ${message}`);
      }
    };
    void poll();
    const id = window.setInterval(() => {
      void poll();
    }, pollingMs);
    return (): void => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [isStreaming, pollingMs, contextFilter]);

  // Autoscroll al fondo cuando llegan líneas nuevas
  useEffect(() => {
    if (!autoscroll) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, autoscroll]);

  const handleClear = (): void => {
    setLines([]);
  };

  const handleScrollNow = (): void => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setAutoscroll(true);
  };

  // Detectar si el usuario hizo scroll arriba — pausar autoscroll
  const handleScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < 16;
    setAutoscroll(atBottom);
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="caption"
            sx={{ fontFamily: "monospace", color: "text.secondary" }}
          >
            $ tail -f production-sync
            {contextFilter ? ` | grep '${contextFilter}'` : ""}
          </Typography>
          <Chip
            label={isStreaming ? "live" : "paused"}
            size="small"
            color={isStreaming ? "success" : "default"}
            variant="outlined"
            sx={{ height: 18, fontSize: "0.65rem" }}
          />
          <Chip
            label={`${lines.length} líneas`}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: "0.65rem" }}
          />
          <Chip
            label="DB + live"
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: "0.65rem" }}
          />
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={isStreaming ? "Pausar polling" : "Reanudar polling"}>
            <IconButton
              size="small"
              onClick={() => setIsStreaming((v) => !v)}
            >
              {isStreaming ? <PauseCircleOutline /> : <PlayCircleOutline />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Bajar al fondo + reactivar autoscroll">
            <IconButton size="small" onClick={handleScrollNow}>
              <VerticalAlignBottom />
            </IconButton>
          </Tooltip>
          <Tooltip title="Limpiar consola">
            <IconButton size="small" onClick={handleClear}>
              <Clear />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {lastError && (
        <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
          {lastError}
        </Alert>
      )}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          backgroundColor: "#0b1020",
          color: "#e2e8f0",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: "0.72rem",
          lineHeight: 1.4,
          p: 1.25,
          borderRadius: 1,
          maxHeight: maxHeightPx,
          minHeight: 120,
          overflow: "auto",
          border: "1px solid #1e293b",
        }}
      >
        {lines.length === 0 ? (
          <Box sx={{ color: "#64748b", fontStyle: "italic" }}>
            (sin eventos recientes; esperando logs durables o live)
          </Box>
        ) : (
          lines.map((l, i) => (
            <Box
              key={`${l.ts}-${i}`}
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: LEVEL_COLOR[l.level],
              }}
            >
              <span style={{ color: "#64748b" }}>{formatTs(l.ts)}</span>
              <span style={{ color: "#475569" }}> [{l.level}] </span>
              <span style={{ color: "#7dd3fc" }}>[{l.context}]</span>{" "}
              {l.message}
            </Box>
          ))
        )}
      </Box>
      {!autoscroll && (
        <Typography
          variant="caption"
          sx={{ color: "warning.main", mt: 0.5, display: "block" }}
        >
          Autoscroll pausado (subiste manualmente). Click en ⬇ para reactivar.
        </Typography>
      )}
    </Box>
  );
}
